// Home, laid out like the Epic web store (no rail: see main.ts) with the launcher's Home sections: a 16:9 hero with
// the featured list beside it (its active row filling as the slide's time runs), one poster row, a wide
// spotlight, then the other rows as three compact columns. On a phone the list folds into dots under the hero and
// the rows scroll sideways.
import { artUrl, comingSoonText, gameById, getCatalogue, isComingSoon, label, type Catalogue, type Game, type List } from "../catalogue.ts";
import { coverStyle, playButton } from "../components.ts";
import { h, link } from "../dom.ts";
import { icon } from "../icons.ts";
import { setTitle } from "../router.ts";

/** seconds each slide stays up and the slide change's length: the launcher's HERO_SECS and TRANS_SECS */
const SLIDE_MS = 8000;
const TRANS_MS = 1150;

export async function homePage(): Promise<HTMLElement> {
  setTitle(null);
  const cat = await getCatalogue();
  const games = (ids: string[]) => ids.map((id) => gameById(cat, id)).filter((g): g is Game => !!g);
  const hero = cat.lists.find((l) => l.style === "hero");
  const featured = games(hero?.games ?? cat.games.slice(0, 5).map((g) => g.id));
  const rest = cat.lists.filter((l) => l.style !== "hero");
  const spot = rest.find((l) => l.style === "spotlight");
  const rows = rest.filter((l) => l.style !== "spotlight");
  const [first, ...others] = rows;
  const columns = others.slice(0, 3);
  const extra = others.slice(3);

  return h(
    "div",
    { class: "home" },
    carousel(featured),
    first ? posterRow(first, games(first.games)) : null,
    spot ? spotlight(games(spot.games)) : null,
    columns.length ? h("div", { class: "columns" }, columns.map((l) => compactColumn(l, games(l.games)))) : null,
    extra.map((l) => posterRow(l, games(l.games))),
    pitch(),
    kinds(cat),
  );
}

/** The launcher's runtime badge: WEB outlined on dark glass, the app runtime solid white. */
export function runtimeBadge(g: Pick<Game, "runtime">): HTMLElement {
  const web = g.runtime === "web";
  return h("span", { class: web ? "rt-badge" : "rt-badge solid" }, web ? "Web" : "App");
}

/** A portrait cover card (launcher `card`: 196×262, radius 12, badge top left, title over the art's foot). */
export function posterCard(g: Game): HTMLElement {
  return h(
    "li",
    { class: "poster" },
    link(
      `/g/${g.id}`,
      { class: "poster-link", "aria-label": `${g.title} by ${g.creator.name}` },
      h(
        "span",
        { class: "poster-art", style: coverStyle(g) },
        runtimeBadge(g),
        isComingSoon(g) ? h("span", { class: "badge warn poster-soon" }, comingSoonText(g)) : null,
        h("span", { class: "poster-title", "aria-hidden": "true" }, g.title),
      ),
      h("span", { class: "poster-name" }, g.title),
      h("span", { class: "poster-by" }, g.creator.name),
    ),
  );
}

function posterRow(l: List, list: Game[]): HTMLElement | null {
  if (list.length === 0) return null;
  const track = h("ul", { class: "poster-track", role: "list" }, list.map(posterCard));
  const page = (dir: number) =>
    track.scrollBy({ left: dir * track.clientWidth * 0.8, behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  return h(
    "section",
    { class: "shelf", "aria-label": l.title },
    h(
      "div",
      { class: "row-head" },
      link(`/browse`, { class: "row-title" }, h("h2", null, l.title), icon("right", 16)),
      h(
        "div",
        { class: "row-arrows" },
        h("button", { type: "button", class: "round", "aria-label": `Scroll ${l.title} back`, onclick: () => page(-1) }, icon("left", 16)),
        h("button", { type: "button", class: "round", "aria-label": `Scroll ${l.title} on`, onclick: () => page(1) }, icon("right", 16)),
      ),
    ),
    track,
  );
}

function spotlight(list: Game[]): HTMLElement | null {
  const g = list[0];
  if (!g) return null;
  const title = artUrl(g.art, "title");
  return h(
    "article",
    { class: "spotlight", style: coverStyle(g, "full") },
    h(
      "div",
      { class: "spotlight-text" },
      h("p", { class: "eyebrow" }, `Spotlight · ${label(g.genre)}`),
      title ? h("img", { class: "spotlight-title-art", src: title, alt: g.title }) : h("h2", { class: "spotlight-title" }, link(`/g/${g.id}`, null, g.title)),
      h("p", { class: "spotlight-blurb" }, g.blurb),
      h("div", { class: "spotlight-actions" }, playButton(g, "small"), link(`/g/${g.id}`, { class: "btn ghost small" }, "Details")),
    ),
  );
}

/** Epic's compact list (launcher `compact_column`): small cover, title, creator, then "Free" and the runtime badge. */
function compactColumn(l: List, list: Game[]): HTMLElement {
  return h(
    "section",
    { class: "compact" },
    h("h2", null, l.title),
    h(
      "ul",
      { role: "list" },
      list.slice(0, 5).map((g) =>
        h(
          "li",
          null,
          link(
            `/g/${g.id}`,
            { class: "compact-item" },
            h("span", { class: "thumb", style: coverStyle(g) }),
            h(
              "span",
              { class: "compact-text" },
              h("span", { class: "compact-title" }, g.title),
              h("span", { class: "compact-by" }, g.creator.name),
              h("span", { class: "compact-price" }, isComingSoon(g) ? h("span", { class: "badge warn" }, comingSoonText(g)) : "Free", runtimeBadge(g)),
            ),
          ),
        ),
      ),
    ),
  );
}

function pitch(): HTMLElement {
  return h(
    "section",
    { class: "pitch" },
    h(
      "div",
      null,
      h("h2", null, "Good games are bangers."),
      h("p", null, "One library for the games scattered across the web. Follow the people who make them, see what your friends play, and pick up where you left off."),
    ),
    h("div", { class: "pitch-actions" }, link("/download", { class: "btn" }, icon("download", 16), "Get Banger"), link("/browse", { class: "btn ghost" }, "Browse games")),
  );
}

function kinds(cat: Catalogue): HTMLElement {
  const counts = cat.taxonomy.kinds.map((k) => [k, cat.games.filter((g) => g.kind === k).length] as const).filter(([, n]) => n > 0);
  return h(
    "section",
    { class: "section" },
    h("h2", null, "Find something to play"),
    h(
      "ul",
      { class: "kind-tiles" },
      counts.map(([k, n]) =>
        h("li", null, link(`/browse?kind=${k}`, { class: "kind-tile" }, h("strong", null, { game: "Games", experience: "Experiences", learn: "Learn", example: "Examples" }[k] ?? k), h("span", null, `${n}`))),
      ),
    ),
  );
}

/**
 * The featured hero, laid out like the Epic web store's: the hero with the featured list beside it (wide screens; the
 * active row fills left to right as the slide's time runs) and round prev/next arrows under the list, dots too on
 * narrow screens. Auto-advances every 8 s and pauses on hover/focus.
 *
 * Slide changes are the launcher's (launcher/src/ui.rs `home`, TRANS_SECS): the outgoing art slides left and blurs
 * out while its eyebrow, title, blurb and buttons leave on staggered delays, then the incoming art comes in from the
 * right, sharpening, its text following with the same stagger. The timings live in style.css (`.slide.enter`,
 * `.slide.leaving`); TRANS_MS here only says when the outgoing slide can be hidden. It runs whatever the viewer's
 * reduced-motion setting, as the launcher's and Epic's store's do (owner, 2026-10-17).
 */
export function carousel(list: Game[]): HTMLElement {
  if (list.length === 0) return h("div");
  let index = 0;
  let timer = 0;
  let leaving: { slide: HTMLElement; done: number } | null = null;
  // the text's four parts, which the transition staggers (style.css: `.part:nth-child(n)`)
  const part = (...kids: HTMLElement[]) => h("div", { class: "part" }, ...kids);
  const slides = list.map((g, i) => {
    const title = artUrl(g.art, "title");
    return h(
      "div",
      {
        class: "slide",
        role: "group",
        "aria-roledescription": "slide",
        "aria-label": `${i + 1} of ${list.length}: ${g.title}`,
        "aria-hidden": i === 0 ? "false" : "true",
      },
      h("div", { class: "slide-bg", style: coverStyle(g, "full") }),
      h(
        "div",
        { class: "slide-text" },
        part(h("p", { class: "eyebrow" }, `Featured · ${label(g.genre)}`)),
        part(title ? h("img", { class: "slide-title-art", src: title, alt: g.title }) : h("h2", { class: "slide-title" }, g.title)),
        part(h("p", { class: "slide-blurb" }, g.blurb)),
        part(
          h("div", { class: "slide-actions" }, playButton(g), link(`/g/${g.id}`, { class: "btn ghost", tabindex: i === 0 ? null : "-1" }, "Details"), runtimeBadge(g)),
          h("p", { class: "slide-by" }, "by ", link(`/c/${g.creator.id}`, { tabindex: i === 0 ? null : "-1" }, g.creator.name)),
        ),
      ),
    );
  });
  const items = list.map((g, i) =>
    h(
      "button",
      { type: "button", class: "feature", "aria-label": `Show ${g.title}`, "aria-current": i === 0 ? "true" : "false", onclick: () => show(i, true) },
      h("span", { class: "feature-fill", "aria-hidden": "true" }),
      h("span", { class: "thumb", style: coverStyle(g) }),
      h("span", { class: "feature-title" }, g.title),
    ),
  );
  const dots = list.map((g, i) =>
    h("button", { class: "dot", type: "button", "aria-label": `Show ${g.title}`, "aria-current": i === 0 ? "true" : "false", onclick: () => show(i, true) }),
  );
  const live = h("div", { class: "sr-only", "aria-live": "polite" });
  const restart = (el: HTMLElement, cls: string) => {
    el.classList.remove(cls);
    void el.offsetWidth; // restart its CSS animations
    el.classList.add(cls);
  };
  const restartFill = () => {
    const fill = items[index]?.querySelector<HTMLElement>(".feature-fill");
    if (!fill) return;
    fill.style.animation = "none";
    void fill.offsetWidth; // restart the CSS animation
    fill.style.animation = "";
  };
  const show = (i: number, user = false) => {
    const next = (i + list.length) % list.length;
    if (next === index) return;
    const from = slides[index]!;
    const to = slides[next]!;
    // one transition at a time, as the launcher's single `trans`: a pick mid-change drops the slide still leaving
    // and the one coming in starts leaving from where it is
    if (leaving) {
      window.clearTimeout(leaving.done);
      leaving.slide.classList.remove("leaving");
    }
    from.classList.remove("on", "enter");
    restart(from, "leaving");
    leaving = { slide: from, done: window.setTimeout(() => (from.classList.remove("leaving"), (leaving = null)), TRANS_MS) };
    to.classList.remove("leaving");
    to.classList.add("on");
    restart(to, "enter");
    index = next;
    slides.forEach((s, j) => {
      s.setAttribute("aria-hidden", j === index ? "false" : "true");
      s.querySelectorAll("a").forEach((a) => (j === index ? a.removeAttribute("tabindex") : a.setAttribute("tabindex", "-1")));
    });
    for (const set of [dots, items]) set.forEach((d, j) => d.setAttribute("aria-current", j === index ? "true" : "false"));
    restartFill();
    if (user) {
      live.textContent = `${list[index]!.title}`;
      if (!root.classList.contains("paused")) start(); // a full slide's time after a pick
    }
  };
  slides[0]!.classList.add("on");
  const stop = () => {
    window.clearInterval(timer);
    root.classList.add("paused");
  };
  const start = () => {
    window.clearInterval(timer);
    if (list.length < 2) return;
    root.classList.remove("paused");
    restartFill();
    timer = window.setInterval(() => {
      if (!root.isConnected) return window.clearInterval(timer);
      show(index + 1);
    }, SLIDE_MS);
  };
  const root: HTMLElement = h(
    "section",
    { class: `carousel${list.length < 2 ? " still" : ""}`, "aria-roledescription": "carousel", "aria-label": "Featured" },
    h(
      "div",
      { class: "hero", onmouseenter: () => stop(), onmouseleave: () => start(), onfocusin: () => stop(), onfocusout: () => start() },
      h("div", { class: "slides" }, slides, h("div", { class: "slides-scrim", "aria-hidden": "true" })),
    ),
    list.length > 1 ? h("div", { class: "features", role: "group", "aria-label": "Featured games" }, items) : null,
    list.length > 1
      ? h(
          "div",
          { class: "carousel-controls" },
          h("div", { class: "dots" }, dots),
          h("button", { class: "round", type: "button", "aria-label": "Previous", onclick: () => show(index - 1, true) }, icon("left", 16)),
          h("button", { class: "round", type: "button", "aria-label": "Next", onclick: () => show(index + 1, true) }, icon("right", 16)),
        )
      : null,
    live,
  );
  root.style.setProperty("--slide-ms", `${SLIDE_MS}ms`);
  root.style.setProperty("--trans-ms", `${TRANS_MS}ms`);
  // swipe on touch screens
  let x0: number | null = null;
  root.addEventListener("touchstart", (e) => (x0 = e.touches[0]?.clientX ?? null), { passive: true });
  root.addEventListener("touchend", (e) => {
    const x1 = e.changedTouches[0]?.clientX;
    if (x0 !== null && x1 !== undefined && Math.abs(x1 - x0) > 40) show(index + (x1 < x0 ? 1 : -1), true);
    x0 = null;
  });
  start();
  return root;
}
